import type { PairedServer, ServerList } from './serverList'
import { toServerList } from './serverList'

/**
 * Did this devices frame say anything the rail has to re-ask about?
 *
 * WHY THIS EXISTS — a loop I built and the owner found in minutes:
 *
 *   devices frame ─► invalidate ['idle-status'] and ['agents']
 *                 ─► both refetch, and each asks EVERY machine, one relay
 *                    request apiece (`/account/devices/{id}/request`)
 *                 ─► relay_request calls watchers.poke(owner)  (app.py:1760)
 *                 ─► devices frame ─► …
 *
 * Self-amplifying, because one frame produced 2 queries × N machines of traffic
 * and every one of those produced another frame. The owner's report is the exact
 * signature: sign in, the rail fills correctly, and a few seconds later every
 * machine reads as disconnected and the terminal never attaches at all.
 *
 * The invalidation itself is right — a machine coming back has to re-populate the
 * rail, and there is no other signal that it did. What was wrong was treating
 * EVERY frame as news. The cloud pokes its watchers for many reasons, including
 * ones we caused, so the client has to decide whether the frame changed anything
 * before spending N requests on it.
 *
 * `last_seen_age_s` is deliberately NOT in the signature. It is derived from a
 * clock and differs on nearly every frame, so including it would compare equal
 * approximately never and leave the loop exactly as it was — the field most
 * likely to look like "something changed" is the one that carries no news at all.
 */
/** Cannot occur in a device id, a name, a reachability word or a channel hash. */
const FIELD_SEP = '\u0000'
const RECORD_SEP = '\u0001'

export function devicesSignature(list: ServerList): string {
  if (list.kind !== 'servers') return list.kind
  return list.servers
    .map((s: PairedServer) =>
      [
        s.server_id,
        s.name,
        // Three-valued, and the whole point: online/offline/unknown is what makes
        // a machine's agents worth asking for again.
        s.reachable,
        /*
         * The roster the machine reports. A session appearing or ending IS news
         * and should still cost a refetch.
         *
         * `null` and `[]` are kept APART, because the type keeps them apart for a
         * reason the rail depends on: null is "the machine has not told us", `[]`
         * is "nothing is running". Collapsing them would make a machine going from
         * silent to genuinely empty compare equal, and the rail would go on showing
         * agents that have ended.
         */
        s.agents === null ? '<null>' : s.agents.map((a) => a.channel_hash).sort().join(','),
      // Field separator, and record separator below — both written as escapes,
      // never as the raw byte. A literal control character is invisible in an
      // editor and any formatter or patch round-trip eats it with no error, and
      // it is the only thing keeping ("a","bc") apart from ("ab","c").
      // avengents-server lost four of these to a port and found them only because
      // `file` called the source "data"; grep had been skipping those files
      // silently for weeks.
      ].join(FIELD_SEP),
    )
    .sort()
    .join(RECORD_SEP)
}

/** The two things a devices frame does, so a test can watch both. */
export type DevicesSink = {
  /** Always: the frame IS the device list, and that half is cheap. */
  setDevices: (list: ServerList) => void
  /** Only when the frame changed something: re-ask the machines. */
  refetchRail: () => void
}

/**
 * The whole handler, gate included — deliberately not a gate you call separately.
 *
 * The gate used to sit as its own line inside the subscription in App.tsx:
 *
 *     if (!devicesGate.current(list)) return
 *
 * Deleting that one line brought back the feedback loop that took the app down,
 * and every test stayed green. I could have written a test for that line; what I
 * could not write was a test that stays true when someone tidies the effect. So
 * the line is gone instead: there is nothing left in App.tsx between receiving a
 * frame and this function, and the only remaining edit that reintroduces the loop
 * is deleting the subscription itself — which also stops the device list updating,
 * a loud failure rather than a silent one.
 *
 * A dangerous edit made impossible beats a dangerous edit made detectable.
 */
export function makeDevicesHandler(sink: DevicesSink): (devices: unknown[]) => void {
  let last: string | null = null
  return (devices: unknown[]) => {
    const list = toServerList(devices)
    sink.setDevices(list)
    const sig = devicesSignature(list)
    if (sig === last) return
    last = sig
    sink.refetchRail()
  }
}
